import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, ArrowLeft, Grid, List, Filter, SortDescending, 
  Zap, Clock, TrendingUp, Tag
} from 'lucide-react';
import { COLORS } from '../utils/colors';
import { SearchContext } from '../App';
import { getTrendUrl } from '../utils/slugs';
import TrendCard from './TrendCard';

const SearchResults = ({ layout = 'grid', showFilters = true }) => {
  const { searchQuery, setSearchQuery, searchResults } = useContext(SearchContext) || {};
  const [viewMode, setViewMode] = useState(layout);
  const [sortBy, setSortBy] = useState('relevance');
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();
  
  // Get unique categories for filtering
  const categories = searchResults ? [...new Set(searchResults.map(trend => trend.category))] : [];
  
  // Handle trend selection
  const handleSelectTrend = (trendId) => {
    const selectedTrend = searchResults?.find(t => t.id === trendId);
    if (selectedTrend) {
      navigate(getTrendUrl(selectedTrend));
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
  };
  
  // Filter results based on active category
  const getFilteredResults = () => {
    if (!searchResults) return [];
    if (activeCategory === 'all') return searchResults;
    return searchResults.filter(trend => trend.category === activeCategory);
  };
  
  // Sort results based on selection
  const getSortedResults = () => {
    const filtered = getFilteredResults();
    
    if (sortBy === 'relevance') return filtered;
    
    const sorted = [...filtered];
    
    switch (sortBy) {
      case 'maturity-high':
        return sorted.sort((a, b) => b.maturity - a.maturity);
      case 'maturity-low':
        return sorted.sort((a, b) => a.maturity - b.maturity);
      case 'timeHorizon':
        const horizonOrder = { 'Short-term': 1, 'Medium-term': 2, 'Long-term': 3 };
        return sorted.sort((a, b) => horizonOrder[a.timeHorizon] - horizonOrder[b.timeHorizon]);
      default:
        return sorted;
    }
  };
  
  const sortedResults = getSortedResults();
  
  if (!searchQuery) {
    return null;
  }
  
  return (
    <div className="w-full animate-fadeIn">
      {/* Search header with stats and filters */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Search Results
            </h2>
            <p className="text-gray-600">
              Found {searchResults?.length || 0} {searchResults?.length === 1 ? 'trend' : 'trends'} for "{searchQuery}"
            </p>
          </div>
          
          <button
            onClick={clearSearch}
            className="mt-3 sm:mt-0 inline-flex items-center text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Clear Search
          </button>
        </div>
        
        {/* Category filters */}
        {showFilters && categories.length > 1 && (
          <div className="mb-4 overflow-x-auto hide-scrollbar">
            <div className="flex space-x-2 pb-2">
              <button
                className={`px-3 py-1.5 rounded-lg transition-all whitespace-nowrap text-sm ${
                  activeCategory === 'all' 
                  ? 'text-white' 
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-red-300'
                }`}
                style={{ backgroundColor: activeCategory === 'all' ? COLORS.primary : '' }}
                onClick={() => setActiveCategory('all')}
              >
                All Categories
              </button>
              
              {categories.map(category => (
                <button
                  key={category}
                  className={`px-3 py-1.5 rounded-lg transition-all whitespace-nowrap text-sm flex items-center gap-1 ${
                    activeCategory === category 
                    ? 'text-white' 
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-red-300'
                  }`}
                  style={{ backgroundColor: activeCategory === category ? COLORS.primary : '' }}
                  onClick={() => setActiveCategory(category)}
                >
                  <Tag className="w-3.5 h-3.5" />
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Controls for view and sort */}
        {showFilters && searchResults?.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 rounded-xl p-3 border border-gray-200">
            {/* View toggle */}
            <div className="flex items-center space-x-2 mb-3 sm:mb-0">
              <span className="text-sm text-gray-700">View:</span>
              <button
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            
            {/* Sort control */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center text-gray-700">
                <List className="w-4 h-4 mr-1" />
                <span className="text-sm mr-2">Sort by:</span>
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white text-gray-700 text-sm rounded-lg border border-gray-300 py-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
              >
                <option value="relevance">Relevance</option>
                <option value="maturity-high">Maturity (High to Low)</option>
                <option value="maturity-low">Maturity (Low to High)</option>
                <option value="timeHorizon">Time Horizon</option>
              </select>
            </div>
          </div>
        )}
      </div>
      
      {/* Search results grid */}
      {sortedResults.length > 0 ? (
        <div className={
          viewMode === 'grid' 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" 
            : "space-y-4"
        }>
          {sortedResults.map(trend => (
            <TrendCard 
              key={trend.id} 
              trend={trend} 
              onSelect={handleSelectTrend} 
              layout={viewMode}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            We couldn't find any trends matching "{searchQuery}".
            Try different keywords or browse all trends.
          </p>
          <button
            onClick={clearSearch}
            className="px-5 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: COLORS.primary }}
          >
            Browse All Trends
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;